import type { PlanOut, PlanPrice } from "@/lib/api";

export type Interval = "MONTHLY" | "YEARLY" | "WEEKLY";

export const INTERVAL_SUFFIX: Record<string, string> = { MONTHLY: "/month", YEARLY: "/year", WEEKLY: "/week" };

/** Prices for a plan, tolerating older APIs that only sent one price. */
export function planPrices(plan: PlanOut): PlanPrice[] {
  if (plan.prices && plan.prices.length) return plan.prices;
  if (plan.price_version_id && plan.price_cents != null) {
    return [
      {
        price_version_id: plan.price_version_id,
        price_cents: plan.price_cents,
        currency: plan.currency ?? "USD",
        billing_interval: plan.billing_interval ?? "MONTHLY",
        paddle_price_id: plan.paddle_price_id,
        promo: plan.promo ?? null,
      },
    ];
  }
  return [];
}

/** Intervals any plan sells, monthly first. */
export function availableIntervals(plans: PlanOut[]): Interval[] {
  const set = new Set(plans.flatMap((p) => planPrices(p).map((x) => x.billing_interval)));
  return (["MONTHLY", "YEARLY", "WEEKLY"] as Interval[]).filter((i) => set.has(i));
}

/** The plan as it looks for one interval: its price fields swapped to that
 *  interval's price (or the plan's default price if it doesn't sell it). */
export function planForInterval(plan: PlanOut, interval: Interval): PlanOut {
  const match = planPrices(plan).find((p) => p.billing_interval === interval);
  if (!match) return plan;
  return {
    ...plan,
    price_version_id: match.price_version_id,
    price_cents: match.price_cents,
    currency: match.currency,
    billing_interval: match.billing_interval,
    paddle_price_id: match.paddle_price_id,
    promo: match.promo ?? null,
  };
}

export function fallbackPrice(cents: number | null, currency: string | null): string {
  if (cents == null) return "—";
  const amount = cents / 100;
  const symbol = !currency || currency === "USD" ? "$" : `${currency} `;
  return `${symbol}${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
}
